/**
 * Local persistence in IndexedDB. Zero dependencies; every call is promisified.
 *
 * Stores:
 *   maps  — LocalMap records (metadata + document), keyed by id
 *   blobs — image blobs keyed by `${mapId}:orig` and `${mapId}:thumb`
 *   kv    — small settings / sync cursors
 */
import type { MapDoc, MapKind } from '../../shared/doc.ts';

export interface LocalMap {
  id: string;
  kind: MapKind;
  title: string;
  createdAt: number; // epoch ms
  updatedAt: number; // epoch ms, bumped on every local change
  deletedAt: number | null;
  rev: number; // last server revision we know about (0 = never uploaded)
  dirty: boolean; // meta/doc needs uploading
  imageType: string; // e.g. image/png
  imageBytes: number;
  imageUploaded: boolean;
  /** false: this device holds a newer thumbnail than the server (it goes up when the map is left). */
  thumbUploaded: boolean;
  /** The server's thumbnail revision as last heard, and the revision of the thumbnail held here. */
  thumbRev?: number | undefined;
  thumbHave?: number | undefined;
  /** Moves whenever the thumbnail held here changes (what the library keys its picture on). */
  thumbStamp?: number | undefined;
  /** The server refused this version (why): it is not sent again until the map changes. */
  blocked?: string | undefined;
  doc: MapDoc;
}

const DB_NAME = 'mapmark';
const DB_VERSION = 1;

let dbPromise: Promise<IDBDatabase> | null = null;

function req<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error ?? new Error('IndexedDB request failed'));
  });
}
function txDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
    tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
  });
}

export function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const r = indexedDB.open(DB_NAME, DB_VERSION);
    r.onupgradeneeded = () => {
      const db = r.result;
      if (!db.objectStoreNames.contains('maps')) {
        const maps = db.createObjectStore('maps', { keyPath: 'id' });
        maps.createIndex('updatedAt', 'updatedAt');
      }
      if (!db.objectStoreNames.contains('blobs')) db.createObjectStore('blobs');
      if (!db.objectStoreNames.contains('kv')) db.createObjectStore('kv');
    };
    r.onsuccess = () => {
      const db = r.result;
      db.onversionchange = () => {
        db.close();
        dbPromise = null;
      };
      resolve(db);
    };
    r.onerror = () => reject(r.error ?? new Error('IndexedDB open failed'));
    r.onblocked = () => reject(new Error('IndexedDB open blocked'));
  });
  dbPromise.catch(() => {
    dbPromise = null;
  });
  return dbPromise;
}

export async function getMap(id: string): Promise<LocalMap | undefined> {
  const db = await openDb();
  return req(db.transaction('maps').objectStore('maps').get(id)) as Promise<LocalMap | undefined>;
}
export async function putMap(map: LocalMap): Promise<void> {
  const db = await openDb();
  const tx = db.transaction('maps', 'readwrite');
  tx.objectStore('maps').put(map);
  await txDone(tx);
}
/**
 * Read, change and write one map in a single transaction. Every writer goes through here: the
 * editor saving and a sync finishing at the same moment can then never undo one another (each
 * sees what the other wrote). `fn` is synchronous — a transaction does not wait. It returns the
 * record to store, `null` to delete the map (and its images), or `undefined` to leave it alone.
 * `blobs` are stored in the same transaction.
 */
export async function mutateMap(id: string, fn: (cur: LocalMap | undefined) => LocalMap | null | undefined, blobs: Record<string, Blob> = {}): Promise<LocalMap | null | undefined> {
  const bufs: [string, StoredBlob][] = [];
  for (const [key, blob] of Object.entries(blobs)) bufs.push([key, { type: blob.type, buf: await blob.arrayBuffer() }]);
  const db = await openDb();
  const tx = db.transaction(['maps', 'blobs'], 'readwrite');
  const done = txDone(tx);
  let out: LocalMap | null | undefined;
  try {
    const cur = (await req(tx.objectStore('maps').get(id))) as LocalMap | undefined;
    out = fn(cur);
    if (out === null) {
      tx.objectStore('maps').delete(id);
      tx.objectStore('blobs').delete(`${id}:orig`);
      tx.objectStore('blobs').delete(`${id}:thumb`);
    } else if (out !== undefined) {
      tx.objectStore('maps').put(out);
      for (const [key, value] of bufs) tx.objectStore('blobs').put(value, key);
    }
  } catch (e) {
    try {
      tx.abort();
    } catch {
      /* already finished */
    }
    await done.catch(() => {});
    throw e;
  }
  await done;
  return out;
}
export async function hasBlob(key: string): Promise<boolean> {
  const db = await openDb();
  return (await req(db.transaction('blobs').objectStore('blobs').count(key))) > 0;
}
export async function listMaps(): Promise<LocalMap[]> {
  const db = await openDb();
  const all = (await req(db.transaction('maps').objectStore('maps').getAll())) as LocalMap[];
  return all.sort((a, b) => b.createdAt - a.createdAt);
}
export async function deleteMapRecord(id: string): Promise<void> {
  const db = await openDb();
  const tx = db.transaction(['maps', 'blobs'], 'readwrite');
  tx.objectStore('maps').delete(id);
  tx.objectStore('blobs').delete(`${id}:orig`);
  tx.objectStore('blobs').delete(`${id}:thumb`);
  await txDone(tx);
}

interface StoredBlob {
  type: string;
  buf: ArrayBuffer;
}

/** Blobs are stored as ArrayBuffers: WebKit cannot persist Blob values in every session type. */
export async function getBlob(key: string): Promise<Blob | undefined> {
  const db = await openDb();
  const v = (await req(db.transaction('blobs').objectStore('blobs').get(key))) as StoredBlob | Blob | undefined;
  if (!v) return undefined;
  if (v instanceof Blob) return v;
  return new Blob([v.buf], { type: v.type });
}
export async function putBlob(key: string, blob: Blob): Promise<void> {
  const buf = await blob.arrayBuffer();
  const db = await openDb();
  const tx = db.transaction('blobs', 'readwrite');
  const value: StoredBlob = { type: blob.type, buf };
  tx.objectStore('blobs').put(value, key);
  await txDone(tx);
}
export async function deleteBlob(key: string): Promise<void> {
  const db = await openDb();
  const tx = db.transaction('blobs', 'readwrite');
  tx.objectStore('blobs').delete(key);
  await txDone(tx);
}

export async function getKV<T>(key: string): Promise<T | undefined> {
  const db = await openDb();
  return req(db.transaction('kv').objectStore('kv').get(key)) as Promise<T | undefined>;
}
export async function setKV(key: string, value: unknown): Promise<void> {
  const db = await openDb();
  const tx = db.transaction('kv', 'readwrite');
  tx.objectStore('kv').put(value, key);
  await txDone(tx);
}

/** Ask the browser not to evict our data (best effort). */
export async function requestPersistence(): Promise<boolean> {
  try {
    if (navigator.storage?.persist) return await navigator.storage.persist();
  } catch {
    /* ignore */
  }
  return false;
}
